/**
 * Shared utility functions for gift box and personalisation features.
 * Loaded before gift-option-buttons.js, gift-box-add-to-cart.js,
 * cart-gifting-drawer.js, and personalisation-drawer.js.
 *
 * Usage: window.GiftUtils.fetchCart(), window.GiftUtils.log(), etc.
 */
;(function () {
  'use strict'

  var GiftUtils = {}

  // --- Debug logging ---

  window.__GIFTPROPS_DEBUG__ = false

  /**
   * Conditional debug logger for gift box operations.
   * Enable at runtime: window.__GIFTPROPS_DEBUG__ = true
   * @param {string} message - Description of what is being logged
   * @param {*} [payload] - Optional data to inspect
   */
  GiftUtils.log = function (message, payload) {
    if (!window.__GIFTPROPS_DEBUG__) return
    if (payload !== undefined) {
      console.warn('%c[GiftBox Debug] ' + message, 'color:#8A2BE2;font-weight:bold', payload)
    } else {
      console.warn('%c[GiftBox Debug] ' + message, 'color:#8A2BE2;font-weight:bold')
    }
  }

  // --- Cart API ---

  /**
   * Fetch the current cart state from /cart.js
   * @returns {Promise<Object>} Parsed cart JSON
   */
  GiftUtils.fetchCart = async function () {
    var res = await fetch('/cart.js', {
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
    if (!res.ok) throw new Error('/cart.js returned ' + res.status)
    return res.json()
  }

  /**
   * POST to a Shopify cart endpoint.
   * @param {string} endpoint - One of '/cart/add.js', '/cart/change.js', '/cart/update.js'
   * @param {Object} data - The request body (will be JSON.stringify'd)
   * @returns {Promise<Response>} The raw fetch Response
   */
  GiftUtils.cartPost = async function (endpoint, data) {
    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(data),
    })
  }

  // --- Cart item lookup ---

  /**
   * Find an addon item in the cart matching a groupId or parentVariantId.
   * @param {Array} cartItems - Array of cart item objects from /cart.js
   * @param {string|null} groupId - The _groupId property to match
   * @param {string|null} parentVariantId - The _parentPid to match as fallback
   * @param {string} [addonType] - Optional addon type filter ('Gift Box', 'Personalisation')
   * @returns {Object|null} The matching addon cart item, or null
   */
  GiftUtils.findAddon = function (cartItems, groupId, parentVariantId, addonType) {
    var filterAddon = function (item) {
      var props = item.properties || {}
      var isAddon = props._addon === 'true' || props._addon === true
      if (!isAddon) return false
      if (addonType && props._addonType !== addonType) return false
      return true
    }

    // Prefer groupId match (most specific)
    if (groupId) {
      var byGroupId = cartItems.find(function (item) {
        if (!filterAddon(item)) return false
        return item.properties._groupId === String(groupId)
      })
      if (byGroupId) return byGroupId
    }

    // Fallback: parentVariantId (for legacy items without groupId)
    if (parentVariantId) {
      return cartItems.find(function (item) {
        if (!filterAddon(item)) return false
        return item.properties._parentPid === String(parentVariantId)
      }) || null
    }

    return null
  }

  /**
   * Convenience alias for finding Gift Box addons specifically.
   */
  GiftUtils.findGiftBoxAddon = function (cartItems, groupId, parentVariantId) {
    return GiftUtils.findAddon(cartItems, groupId, parentVariantId, 'Gift Box')
  }

  // --- Group ID ---

  /**
   * Generate a unique group ID for linking main products with addon products.
   * @returns {string} A timestamp-based unique ID
   */
  GiftUtils.generateGroupId = function () {
    return Date.now().toString()
  }

  // --- DOM / UI helpers ---

  /**
   * Toggle body scroll lock for drawer overlays.
   * @param {boolean} lock - true to prevent scrolling, false to restore it
   */
  GiftUtils.setBodyOverflowHidden = function (lock) {
    var method = lock ? 'add' : 'remove'
    document.body.classList[method]('overflow-hidden--drawer-open')
    document.documentElement.classList[method]('overflow-hidden--drawer-open')
  }

  /**
   * Toggle the loading/disabled state on the cart items container.
   * @param {boolean} loading - true to show loading state, false to remove it
   * @returns {HTMLElement|null} The container element
   */
  GiftUtils.setCartLoading = function (loading) {
    var container =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems')
    if (container) {
      container.classList[loading ? 'add' : 'remove']('cart__items--disabled')
    }
    return container
  }

  /**
   * Parse an HTML string and extract innerHTML of a given selector.
   * @param {string} html - Raw HTML string
   * @param {string} selector - CSS selector to find within parsed document
   * @returns {string} innerHTML of matched element
   * @throws {Error} if selector is not found in the parsed HTML
   */
  GiftUtils.parseSectionHTML = function (html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html')
    var el = doc.querySelector(selector)
    if (!el) {
      throw new Error('parseSectionHTML: selector not found: ' + selector)
    }
    return el.innerHTML
  }

  /**
   * Trigger the cart drawer/items custom element to refresh its UI.
   * Calls onCartUpdate() on whichever cart element exists in the DOM.
   */
  GiftUtils.refreshCartUI = function () {
    var cartDrawerItems =
      document.querySelector('cart-drawer-items') || document.querySelector('cart-items')
    if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
      cartDrawerItems.onCartUpdate()
    }
  }

  window.GiftUtils = GiftUtils
})()
